import { useNavigate } from 'react-router-dom'
import { useLocation, useParams } from 'react-router-dom'

import { Box, Button, TextSpinner } from '@factorialco/deprecated-design-system'
import { DuplicateProjectDocument, DuplicateProjectMutation } from 'generated/resources/graphql'
import moment from 'moment'

import { observer } from 'mobx-react'

import DayPicker from 'modules/core/components/DayPicker'
import Modal from 'modules/core/components/Modal'
import TextField from 'modules/core/components/TextField'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { dateUIToBackend } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { DuplicateOptions } from './DuplicateOptions'

export interface DuplicateProjectFormValues {
  name: string
  code?: string
  description?: string
  startDate?: string
  dueDate?: string
  includeAll?: boolean
  includeProjectWorkers?: boolean
  includeSubprojects?: boolean
  includeProjectTasks?: boolean
  includeProjectTasksAssignees?: boolean
  includeClient?: boolean
}

const DuplicateProjectModal: React.FC = () => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const { projectId } = useParams()
  const { state } = useLocation()
  const { projectName, hasSubprojectsFeature } = state || {
    projectName: '',
    hasSubprojectsFeature: false,
  }

  const [duplicateProject, { loading }] = useMutation(DuplicateProjectDocument, {
    extractorFn: (data: DuplicateProjectMutation) =>
      data.projectManagementMutations.duplicateProjectmanagementProject,
  })

  const onClose = () => {
    track(trackingEvents.projectManagement.projects.duplicateProject.duplicateProjectCancelled)

    navigate(-1)
  }

  const form = useForm<DuplicateProjectFormValues>({
    defaultValues: {
      name: projectName + ' (' + i18n.t('common.copy') + ')' || '',
      code: '',
      description: '',
      startDate: '',
      dueDate: '',
      includeProjectWorkers: false,
      includeSubprojects: false,
      includeClient: false,
    },
    validate: (data) => {
      const errors: FormErrors<DuplicateProjectFormValues> = {}

      if (!data.name) {
        errors.name = i18n.t('project_management.modals.error_messages.mandatory')
      }

      return errors
    },
    onSubmit: async (data, { setErrors }) => {
      track(trackingEvents.projectManagement.projects.duplicateProject.duplicateProjectSubmit, {
        includeSubprojects: !!data.includeSubprojects,
        includeProjectWorkers: !!data.includeProjectWorkers,
      })

      track(trackingEvents.projectManagement.projects.duplicateProject.duplicateProjectOptions, {
        including: !!data.includeAll ? 'All' : 'Individually',
      })
      !data.includeAll &&
        track(trackingEvents.projectManagement.projects.duplicateProject.duplicateIndividually, {
          Assignees: !!data.includeProjectWorkers,
          Subprojects: !!data.includeSubprojects,
          Tasks: !!data.includeProjectTasks,
          'Task Assignees': !!data.includeProjectTasksAssignees,
        })
      const result = await duplicateProject({
        variables: {
          toBeDuplicatedId: Number(projectId),
          name: data.name,
          code: data.code || undefined,
          description: data.description || undefined,
          startDate: data.startDate ? dateUIToBackend(data.startDate) : undefined,
          dueDate: data.dueDate ? dateUIToBackend(data.dueDate) : undefined,
          includeClient: !!data.includeClient,
          includeProjectWorkers: !!data.includeProjectWorkers,
          includeSubprojects: !!data.includeSubprojects,
          includeProjectTasks: !!data.includeProjectTasks,
          includeProjectTasksAssignees: !!data.includeProjectTasksAssignees,
        },
      })

      const handled = handleResult(result, (errors) => {
        if (
          errors.name &&
          i18n.t('project_management.modals.error_messages.project_name_exists') !== undefined
        )
          errors.name = i18n.t('project_management.modals.error_messages.project_name_exists')
        if (
          errors.code &&
          i18n.t('project_management.modals.error_messages.project_code_exists') !== undefined
        )
          errors.code = i18n.t('project_management.modals.error_messages.project_code_exists')

        setErrors(errors)
      })

      if (handled.success) {
        track(trackingEvents.projectManagement.projects.duplicateProject.duplicateProjectConfirmed)
        app.setFlash({
          type: 'correct',
          message: i18n.t('project_management.toasts.duplicate_project_success'),
        })
        navigate(
          navigation.projects.id.details.overview.fullPath({
            id: Number(handled.data.project?.id),
          }),
          {
            replace: true,
          }
        )
      } else {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('errors.generic_saving'),
        })
      }

      return handled
    },
  })

  const handleDuplicateOptionsChange = (
    includeAll: boolean | undefined,
    includeSubprojects: boolean | undefined,
    includeProjectWorkers: boolean | undefined,
    includeProjectTasks: boolean | undefined,
    includeProjectTasksAssignees: boolean | undefined,
    includeClient: boolean | undefined
  ) => {
    form.setValues({
      ...form.values,
      includeAll,
      includeSubprojects,
      includeProjectWorkers,
      includeProjectTasks,
      includeProjectTasksAssignees,
      includeClient,
    })
  }

  if (!hasSubprojectsFeature) return null
  return (
    <Modal
      illustration="document-edit"
      title={i18n.t('project_management.modals.duplicate_project.title')}
      description={i18n.t('project_management.modals.duplicate_project.description')}
      onClose={onClose}
      onSubmit={() => {
        form.handleSubmit()
      }}
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button disabled={loading} submit>
            {i18n.t('project_management.modals.duplicate_project.submit')}
          </Button>
        </>
      }
    >
      {loading ? (
        <TextSpinner text={i18n.t('project_management.modals.duplicate_project.spinner_message')} />
      ) : (
        <Box gap="s16">
          <TextField
            label={i18n.t('project_management.modals.duplicate_project.input_labels.project_name')}
            value={form.values.name}
            onChange={(value) => form.setValues({ ...form.values, name: value })}
            error={form.touched.name ? form.errors.name : undefined}
            mandatory
          />
          <TextField
            label={i18n.t('project_management.modals.duplicate_project.input_labels.project_code')}
            value={form.values.code}
            onChange={(value) => form.setValues({ ...form.values, code: value })}
            error={form.touched.code ? form.errors.code : undefined}
          />
          <TextField
            label={i18n.t(
              'project_management.modals.duplicate_project.input_labels.project_description'
            )}
            value={form.values.description}
            onChange={(value) => form.setValues({ ...form.values, description: value })}
            error={form.touched.description ? form.errors.description : undefined}
          />
          <DayPicker
            withNull
            label={i18n.t('project_management.modals.duplicate_project.input_labels.start_date')}
            value={form.values.startDate}
            onChange={(value) => form.setValues({ ...form.values, startDate: value })}
            maxDate={form.values.dueDate ? moment(form.values.dueDate) : undefined}
          />
          <DayPicker
            withNull
            label={i18n.t('project_management.modals.duplicate_project.input_labels.due_date')}
            value={form.values.dueDate}
            onChange={(value) => form.setValues({ ...form.values, dueDate: value })}
            minDate={form.values.startDate ? moment(form.values.startDate) : undefined}
          />
          <DuplicateOptions onOptionsChange={handleDuplicateOptionsChange} />
        </Box>
      )}
    </Modal>
  )
}

export default observer(DuplicateProjectModal)
